import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, formatBytes, formatDate } from '../lib/api.js';
import AdminShell from './AdminShell.jsx';
import Uploader from './Uploader.jsx';
import { StatusBadge, CopyRow } from './Dashboard.jsx';

export default function GalleryEditor() {
  const { id } = useParams();
  const [gallery, setGallery] = useState(null);
  const [thumbs, setThumbs] = useState({});
  const [error, setError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [assigning, setAssigning] = useState(false);
  const dragId = useRef(null);
  // Anchor for shift-click ranges. Filing 567 photographs one click at a time
  // is not a workflow, so selecting a run has to be one gesture.
  const lastClicked = useRef(null);

  const load = useCallback(async () => {
    try {
      const g = await api.get(`/api/admin/galleries/${id}`);
      setGallery(g);
      // Thumbnails now arrive signed with the gallery itself. They used to be
      // fetched one request per asset, which meant 567 round trips on a real
      // wedding and an editor that crawled.
      setThumbs(Object.fromEntries(g.assets.filter((a) => a.thumb_url).map((a) => [a.id, a.thumb_url])));
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // While any film is still encoding on Bunny, poll its status so the row flips
  // from "Processing" to "Ready" on its own without a manual refresh.
  useEffect(() => {
    const processing = (gallery?.assets || []).filter(
      (a) => a.kind === 'video' && a.stream_status !== 'ready' && a.upload_status !== 'complete',
    );
    if (processing.length === 0) return;
    const t = setInterval(async () => {
      await Promise.all(processing.map((a) => api.get(`/api/admin/uploads/${a.id}/stream-status`).catch(() => {})));
      load();
    }, 10000);
    return () => clearInterval(t);
  }, [gallery, load]);

  if (error) return <AdminShell><div className="error">{error}</div></AdminShell>;
  if (!gallery) return <AdminShell><div className="muted">Loading…</div></AdminShell>;

  const patch = async (body) => {
    setGallery(await api.patch(`/api/admin/galleries/${id}`, body).then((g) => ({ ...gallery, ...g })));
  };

  const photos = gallery.assets.filter((a) => a.kind === 'photo' && a.upload_status === 'complete');
  // Films show even while Bunny is still encoding, so the photographer can set
  // titles/thumbnails immediately and watch the processing state.
  const videos = gallery.assets.filter((a) => a.kind === 'video');
  const pending = gallery.assets.filter((a) => a.kind === 'photo' && a.upload_status !== 'complete');
  const clientUrl = `${window.location.origin}/${gallery.slug}`;
  const featuredCount = photos.filter((a) => a.featured).length;
  const sections = gallery.sections || [];
  const sectionName = (sid) => sections.find((s) => s.id === sid)?.name || null;
  // The exact list on screen, so shift-click ranges follow what the eye sees
  // rather than the unfiltered order underneath.
  const shownPhotos = showFeaturedOnly ? photos.filter((a) => a.featured) : photos;

  function toggleSelect(index, shiftKey) {
    const id = shownPhotos[index].id;
    /*
     * Read the anchor BEFORE moving it. A state updater runs when React gets
     * round to it, not when setSelected is called — so a ref mutated on the
     * next line is already the new value by the time the updater looks at it,
     * and every shift-click collapses to a range of one.
     */
    const anchor = lastClicked.current;
    lastClicked.current = index;

    setSelected((prev) => {
      const next = new Set(prev);
      if (shiftKey && anchor !== null) {
        const [from, to] = [anchor, index].sort((a, b) => a - b);
        for (let i = from; i <= to; i += 1) next.add(shownPhotos[i].id);
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  /** File every selected photograph into one section (or back to unfiled). */
  async function assignTo(sectionId) {
    if (!selected.size) return;
    setAssigning(true);
    try {
      await api.post(`/api/admin/galleries/${id}/assign-section`, {
        asset_ids: [...selected],
        section_id: sectionId,
      });
      setSelected(new Set());
      lastClicked.current = null;
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  }

  /**
   * Flip one photo's public-portfolio pick. Updated in place rather than
   * refetching, so working through a 567-photo wedding stays instant.
   */
  async function toggleFeatured(asset) {
    const next = !asset.featured;
    setGallery((g) => ({
      ...g,
      assets: g.assets.map((a) => (a.id === asset.id ? { ...a, featured: next } : a)),
    }));
    try {
      await api.patch(`/api/admin/assets/${asset.id}`, { featured: next });
    } catch (err) {
      // Put it back if the server refused, so the UI never lies about what's public.
      setGallery((g) => ({
        ...g,
        assets: g.assets.map((a) => (a.id === asset.id ? { ...a, featured: !next } : a)),
      }));
      setError(err.message);
    }
  }

  // Drag to reorder. Persisted as an explicit sort_order list, which is also
  // what the client gallery renders by.
  async function commitOrder(nextPhotos) {
    setGallery((g) => ({ ...g, assets: [...nextPhotos, ...videos, ...pending] }));
    await api.put(`/api/admin/galleries/${id}/order`, {
      assetIds: [...nextPhotos, ...videos].map((a) => a.id),
    });
  }

  function onDrop(targetId) {
    const from = photos.findIndex((a) => a.id === dragId.current);
    const to = photos.findIndex((a) => a.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const next = photos.slice();
    next.splice(to, 0, next.splice(from, 1)[0]);
    commitOrder(next);
  }

  return (
    <AdminShell
      actions={
        <a className="btn sm" href={clientUrl} target="_blank" rel="noreferrer">
          Preview client view
        </a>
      }
    >
      <div className="spread" style={{ marginBottom: '1rem' }}>
        <div>
          <Link to="/admin" className="tiny">
            ← All galleries
          </Link>
          <h1 style={{ margin: '0.25rem 0 0' }}>{gallery.client_name}</h1>
          <div className="tiny">{formatDate(gallery.wedding_date)}</div>
        </div>
        <StatusBadge gallery={gallery} />
      </div>

      <div className="stat-grid" style={{ marginBottom: '1.25rem' }}>
        <div className="stat"><b>{gallery.stats.photos}</b><span className="tiny">Photos</span></div>
        <div className="stat"><b>{gallery.stats.videos}</b><span className="tiny">Videos</span></div>
        <div className="stat"><b>{formatBytes(gallery.stats.bytes)}</b><span className="tiny">Storage used</span></div>
        <div className="stat">
          <b>{gallery.view_count}</b>
          <span className="tiny">
            {gallery.view_count === 0 ? 'Not viewed yet' : `Last ${formatDate(gallery.last_viewed_at)}`}
          </span>
        </div>
      </div>

      {/* The couple's name and date are the gallery's identity — the cover, the
          lock screen and the public portfolio all read from them — so a typo
          caught after creation has to be fixable. Editing the name does NOT
          touch the slug, so links already sent keep working; that is what the
          separate "Change link" control below is for. */}
      <div className="card stack" style={{ marginBottom: '1.25rem' }}>
        <h2>Details</h2>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <div className="field grow">
            <label htmlFor="cname">Client name</label>
            <input
              id="cname"
              // Remount when the saved value changes so the uncontrolled input
              // re-syncs after a save (or a rejected blank).
              key={gallery.client_name}
              defaultValue={gallery.client_name}
              placeholder="Ntobeko &amp; Sipho"
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (!v) return void (e.target.value = gallery.client_name);
                if (v !== gallery.client_name) patch({ client_name: v });
              }}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            />
            <p className="tiny" style={{ margin: '0.35rem 0 0' }}>
              Shown on the cover and the lock screen — and in the website portfolio
              if this wedding is public.
            </p>
          </div>
          <div className="field">
            <label htmlFor="wdate">Wedding date</label>
            <input
              id="wdate"
              type="date"
              value={gallery.wedding_date || ''}
              onChange={(e) => patch({ wedding_date: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="card stack" style={{ marginBottom: '1.25rem' }}>
        <h2>Delivery</h2>
        <CopyRow label="Client link" value={clientUrl} />
        <SlugEditor gallery={gallery} onSaved={load} />
        {/* This is destructive and irreversible: it invalidates the password the
            couple already have, and the old one cannot be read back. It also sits
            next to the client link, where someone hunting for the CURRENT password
            will find it first — there is no way to view that (it is hashed), so the
            only password-shaped button is the one that destroys it. Hence the
            confirm, and a result you cannot miss. */}
        <div className="row">
          <button
            className="sm danger"
            onClick={async () => {
              if (
                !window.confirm(
                  'Generate a new password for this gallery?\n\n' +
                    'The password this couple already have will stop working immediately, ' +
                    'and the current one cannot be recovered. You will have to send them the new one.',
                )
              )
                return;
              setNewPassword((await api.post(`/api/admin/galleries/${id}/reset-password`)).password);
            }}
          >
            Generate new password
          </button>
        </div>
        {newPassword && (
          <>
            <CopyRow label="New password — save this now" value={newPassword} />
            <p className="tiny" style={{ margin: '-0.35rem 0 0' }}>
              Shown once. Reload this page and it is gone, and the old password no longer works.
            </p>
          </>
        )}

        <div className="row">
          <label className="row" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={gallery.password_enabled !== 0}
              onChange={(e) => patch({ password_enabled: e.target.checked })}
            />
            <span>Ask for a password</span>
          </label>
        </div>
        <p className="tiny" style={{ margin: '-0.35rem 0 0' }}>
          {gallery.password_enabled === 0
            ? 'Off — anyone with the link can open this gallery. The link stays out of search results, but it is no longer private to the couple.'
            : 'On — the couple need the password above. Once they enter it they stay in until they close their browser.'}
        </p>

        <AccessCodePanel gallery={gallery} patch={patch} onChanged={load} />

        <div className="row">
          <label className="row" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={!!gallery.allow_photo_download}
              onChange={(e) => patch({ allow_photo_download: e.target.checked })}
            />
            <span>Allow full-resolution photo downloads</span>
          </label>
        </div>
        <div className="row">
          <label className="row" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={!!gallery.allow_video_download}
              onChange={(e) => patch({ allow_video_download: e.target.checked })}
            />
            <span>Allow video download (off = stream only)</span>
          </label>
        </div>

        {/* The gallery half of the two-key rule: this alone publishes nothing —
            only photographs individually marked ♥ ever appear publicly. */}
        <div className="row">
          <label className="row" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={!!gallery.is_public}
              onChange={(e) => patch({ is_public: e.target.checked })}
            />
            <span>Show this wedding in my public portfolio</span>
          </label>
        </div>
        {gallery.is_public ? (
          <p className="tiny" style={{ margin: '-0.35rem 0 0' }}>
            {featuredCount > 0
              ? `${featuredCount} photograph${featuredCount === 1 ? '' : 's'} are public. Anyone can see them — no password.`
              : 'Nothing is public yet. Pick photographs with ♥ below to publish them.'}
          </p>
        ) : (
          <p className="tiny" style={{ margin: '-0.35rem 0 0' }}>
            Off — nothing from this wedding is publicly visible.
          </p>
        )}

        <div className="field">
          <label htmlFor="kicker">Cover heading</label>
          <input
            id="kicker"
            defaultValue={gallery.hero_kicker || ''}
            placeholder="The Wedding of"
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (gallery.hero_kicker || '')) patch({ hero_kicker: v });
            }}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          />
          <p className="tiny" style={{ margin: '0.35rem 0 0' }}>
            The small line above the couple's name, on the cover and the lock screen.
            Leave blank for “The Wedding of”.
          </p>
        </div>

        <div className="row" style={{ alignItems: 'flex-start' }}>
          <div className="field grow">
            <label htmlFor="st">Status</label>
            <select id="st" value={gallery.status} onChange={(e) => patch({ status: e.target.value })}>
              <option value="draft">Draft — hidden while you finish uploading</option>
              <option value="delivered">Delivered — ready to send</option>
            </select>
          </div>
          <div className="field grow">
            <label htmlFor="ex">Expires</label>
            <input
              id="ex"
              type="date"
              value={gallery.expires_at || ''}
              onChange={(e) => patch({ expires_at: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="card stack" style={{ marginBottom: '1.25rem' }}>
        <h2>Upload</h2>
        <Uploader galleryId={gallery.id} onComplete={load} />
        {pending.length > 0 && (
          <p className="tiny">
            {pending.length} upload{pending.length === 1 ? '' : 's'} incomplete — they stay hidden from the client until
            finished.
          </p>
        )}
      </div>

      {videos.length > 0 && (
        <div className="card stack" style={{ marginBottom: '1.25rem' }}>
          <div className="spread">
            <h2>Films</h2>
            <span className="tiny hide-sm">Name each film — clients see these titles in the playlist</span>
          </div>
          {videos.map((v) => (
            <VideoRow key={v.id} video={v} onDeleted={load} />
          ))}
        </div>
      )}

      <SectionManager galleryId={id} sections={sections} photos={photos} onChanged={load} />

      <div className="card stack">
        <div className="spread">
          <h2>Photos ({photos.length})</h2>
          <div className="row" style={{ gap: '0.75rem' }}>
            <span className="tiny hide-sm">Drag to reorder · ★ cover · ♥ portfolio</span>
            <button
              className={`sm ${showFeaturedOnly ? 'primary' : ''}`}
              onClick={() => setShowFeaturedOnly((v) => !v)}
            >
              {showFeaturedOnly ? 'Showing portfolio picks' : `Portfolio picks (${featuredCount})`}
            </button>
          </div>
        </div>

        {sections.length > 0 && (
          <div className="assign-bar">
            <span className="tiny">
              {selected.size > 0
                ? `${selected.size} selected`
                : 'Tick photographs to file them — shift-click for a run'}
            </span>
            <span className="row" style={{ gap: '0.4rem', marginBottom: 0 }}>
              <button
                className="sm ghost"
                disabled={assigning}
                onClick={() =>
                  setSelected((prev) =>
                    prev.size === shownPhotos.length ? new Set() : new Set(shownPhotos.map((p) => p.id)),
                  )
                }
              >
                {selected.size === shownPhotos.length && shownPhotos.length > 0 ? 'Clear' : 'Select all'}
              </button>
              <select
                value=""
                disabled={!selected.size || assigning}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v !== '') assignTo(v === 'none' ? null : Number(v));
                  e.target.value = '';
                }}
              >
                <option value="">{assigning ? 'Filing…' : 'File into…'}</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.day_name ? `${s.day_name} · ${s.name}` : s.name}
                  </option>
                ))}
                <option value="none">— Unfiled —</option>
              </select>
            </span>
          </div>
        )}

        <div className="admin-grid">
          {shownPhotos.map((a, index) => (
            <div
              key={a.id}
              className={`admin-tile ${gallery.cover_asset_id === a.id ? 'is-cover' : ''} ${a.featured ? 'is-featured' : ''} ${selected.has(a.id) ? 'is-selected' : ''}`}
              draggable
              onDragStart={() => {
                dragId.current = a.id;
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(a.id)}
            >
              {thumbs[a.id] ? <img src={thumbs[a.id]} alt={a.filename} loading="lazy" /> : <div className="skeleton" style={{ width: '100%', height: '100%' }} />}
              {sections.length > 0 && (
                <button
                  className="tile-check"
                  title={selected.has(a.id) ? 'Deselect' : 'Select'}
                  onClick={(e) => toggleSelect(index, e.shiftKey)}
                >
                  {selected.has(a.id) ? '✓' : ''}
                </button>
              )}
              {sectionName(a.section_id) && <span className="tile-section">{sectionName(a.section_id)}</span>}
              {a.featured && <span className="tile-badge" title="In the public portfolio">♥</span>}
              <div className="tile-actions">
                <button title="Set as cover" onClick={() => patch({ cover_asset_id: a.id })}>
                  ★
                </button>
                <button
                  title={a.featured ? 'Remove from public portfolio' : 'Add to public portfolio'}
                  onClick={() => toggleFeatured(a)}
                >
                  {a.featured ? '♥' : '♡'}
                </button>
                <button title="Delete" onClick={() => remove(a.id, load)}>
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
        {photos.length === 0 && <p className="muted">No photos yet.</p>}
        {showFeaturedOnly && featuredCount === 0 && (
          <p className="muted">No portfolio picks yet — click ♡ on the photographs you want to show publicly.</p>
        )}
      </div>

      <div className="card stack" style={{ marginTop: '1.25rem' }}>
        <h2>Activity</h2>
        {gallery.views.length === 0 ? (
          <p className="muted">The client has not opened this gallery yet.</p>
        ) : (
          <ul className="tiny" style={{ margin: 0, paddingLeft: '1rem' }}>
            {gallery.views.map((v, i) => (
              <li key={i}>{new Date(v.viewed_at + 'Z').toLocaleString()}</li>
            ))}
          </ul>
        )}
        {gallery.downloads.length > 0 && (
          <p className="tiny" style={{ margin: 0 }}>
            Downloads:{' '}
            {gallery.downloads.map((d) => `${d.n} ${d.scope === 'all' ? 'bulk' : 'single-file'} (last ${formatDate(d.last)})`).join(' · ')}
          </p>
        )}
      </div>
    </AdminShell>
  );
}

async function remove(assetId, reload) {
  if (!window.confirm('Delete this file from the gallery and from R2? This cannot be undone.')) return;
  await api.del(`/api/admin/assets/${assetId}`);
  reload();
}

/**
 * How a gallery is broken up for the couple: an optional day, and the parts
 * within it. Two text fields rather than a builder UI — a photographer setting
 * up "Day 1 / Decor" wants to type it, not drag it.
 *
 * Deleting a section never deletes photographs; they return to unfiled and keep
 * showing under "All". The confirm says so, because "delete" next to a grid of
 * photos reads as dangerous whether or not it is.
 */
function SectionManager({ galleryId, sections, photos, onChanged }) {
  const [day, setDay] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [savedDefaults, setSavedDefaults] = useState(false);

  const counts = new Map();
  photos.forEach((p) => {
    if (p.section_id) counts.set(p.section_id, (counts.get(p.section_id) || 0) + 1);
  });
  const unfiled = photos.filter((p) => !p.section_id).length;

  const run = async (fn) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Grouped for display; day '' is the ungrouped bucket.
  const groups = [];
  sections.forEach((s) => {
    const key = s.day_name || '';
    let g = groups.find((x) => x.day === key);
    if (!g) groups.push((g = { day: key, items: [] }));
    g.items.push(s);
  });

  return (
    <div className="card stack" style={{ marginBottom: '1.25rem' }}>
      <div className="spread">
        <h2>Sections</h2>
        <span className="tiny hide-sm">{unfiled} unfiled</span>
      </div>

      {sections.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          None yet. Add "Decor", "Photoshoot" and the rest below, optionally grouped by day — the
          couple then get filter buttons above their photographs instead of one long scroll.
        </p>
      )}

      {groups.map((g) => (
        <div key={g.day || '_'} className="stack" style={{ gap: '0.35rem' }}>
          {g.day && <span className="label">{g.day}</span>}
          {g.items.map((s) => (
            <div className="row" key={s.id} style={{ justifyContent: 'space-between', marginBottom: 0 }}>
              <span>
                {s.name} <span className="tiny">· {counts.get(s.id) || 0} photographs</span>
              </span>
              <span className="row" style={{ gap: '0.3rem', marginBottom: 0 }}>
                <button
                  className="sm ghost"
                  disabled={busy}
                  onClick={() => {
                    const next = window.prompt('Section name', s.name);
                    if (next && next.trim()) run(() => api.patch(`/api/admin/sections/${s.id}`, { name: next.trim() }));
                  }}
                >
                  Rename
                </button>
                <button
                  className="sm ghost"
                  disabled={busy}
                  onClick={() => {
                    const next = window.prompt('Day for this section (leave blank for none)', s.day_name || '');
                    if (next !== null) run(() => api.patch(`/api/admin/sections/${s.id}`, { day_name: next }));
                  }}
                >
                  Day
                </button>
                <button
                  className="sm danger"
                  disabled={busy}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete the section "${s.name}"?\n\nThe photographs in it are NOT deleted — they go back to unfiled and still show under "All".`,
                      )
                    )
                      run(() => api.del(`/api/admin/sections/${s.id}`));
                  }}
                >
                  Delete
                </button>
              </span>
            </div>
          ))}
        </div>
      ))}

      <form
        className="row"
        style={{ marginBottom: 0 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          run(async () => {
            await api.post(`/api/admin/galleries/${galleryId}/sections`, { day_name: day, name });
            setName('');
          });
        }}
      >
        <input
          value={day}
          onChange={(e) => setDay(e.target.value)}
          placeholder="Day (optional)"
          style={{ maxWidth: '11rem' }}
        />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Section, e.g. Decor" />
        <button className="sm primary" disabled={busy || !name.trim()}>
          Add
        </button>
      </form>

      <div className="row" style={{ marginBottom: 0, gap: '0.5rem' }}>
        <button
          className="sm"
          disabled={busy}
          onClick={() => run(() => api.post(`/api/admin/galleries/${galleryId}/sections/seed`))}
        >
          Use my usual sections
        </button>
        <button
          className="sm"
          disabled={busy || sections.length === 0}
          onClick={() =>
            run(async () => {
              await api.put('/api/admin/section-defaults', {
                sections: sections.map((s) => ({ day_name: s.day_name, name: s.name })),
              });
              setSavedDefaults(true);
              setTimeout(() => setSavedDefaults(false), 2000);
            })
          }
        >
          {savedDefaults ? 'Saved' : 'Save these as my usual'}
        </button>
      </div>
      <p className="tiny" style={{ margin: 0 }}>
        "My usual" is reused for every new gallery. Editing this gallery's sections never changes it
        or any other gallery.
      </p>
      {err && <div className="error">{err}</div>}
    </div>
  );
}

/**
 * Edit the gallery's slug — the part of the client link after the domain.
 * Deliberately behind a "Change link" toggle rather than an always-open field:
 * editing it invalidates any link already sent to the couple, so it shouldn't
 * be a thing you fumble into. The server re-cleans whatever is typed and
 * guarantees uniqueness, so the saved value may differ slightly from the input.
 */
function SlugEditor({ gallery, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(gallery.slug);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const host = `${window.location.host}/`;

  async function save() {
    const wanted = value.trim();
    if (!wanted || wanted === gallery.slug) return setOpen(false);
    setBusy(true);
    setError('');
    try {
      const updated = await api.patch(`/api/admin/galleries/${gallery.id}`, { slug: wanted });
      if (updated.slug !== wanted) {
        setError(`That link was taken or contained characters we can't use — saved as "${updated.slug}" instead.`);
      }
      setValue(updated.slug);
      setOpen(false);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="row" style={{ marginTop: '-0.35rem' }}>
        <button className="sm ghost" onClick={() => setOpen(true)}>Change link</button>
        {error && <span className="tiny">{error}</span>}
      </div>
    );
  }

  return (
    <div className="field" style={{ marginBottom: 0 }}>
      <label htmlFor="slug">Custom link</label>
      <div className="row" style={{ flexWrap: 'nowrap', alignItems: 'center' }}>
        <span className="tiny" style={{ whiteSpace: 'nowrap' }}>{host}</span>
        <input
          id="slug"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
          placeholder="amagoduko-ka-msweswe"
        />
        <button className="primary sm" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        <button className="sm ghost" onClick={() => { setValue(gallery.slug); setOpen(false); setError(''); }}>Cancel</button>
      </div>
      <p className="tiny" style={{ margin: '0.4rem 0 0' }}>
        ⚠ Changing this breaks any link you've already sent to this couple.
      </p>
      {error && <div className="error" style={{ marginTop: '0.5rem' }}>{error}</div>}
    </div>
  );
}

/**
 * One film row: a custom-thumbnail well, an inline auto-saving title, the Bunny
 * encoding state, and delete. Films appear here the moment they're uploaded and
 * flip from "Processing" to "Ready" as Bunny finishes encoding.
 */
function VideoRow({ video, onDeleted }) {
  const [title, setTitle] = useState(video.title || '');
  const [thumb, setThumb] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // Load the current thumbnail (poster) if one is set.
  useEffect(() => {
    if (video.preview_key) api.get(`/api/admin/assets/${video.id}/url`).then((r) => setThumb(r.url)).catch(() => {});
  }, [video.id, video.preview_key]);

  async function save() {
    if ((video.title || '') === title.trim()) return;
    await api.patch(`/api/admin/assets/${video.id}`, { title: title.trim() });
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  async function uploadThumb(file) {
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await api.post(`/api/admin/assets/${video.id}/poster-url`);
      const res = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!res.ok) throw new Error('Upload failed');
      setThumb(URL.createObjectURL(file));
    } finally {
      setBusy(false);
    }
  }

  const ready = video.stream_status === 'ready' || video.upload_status === 'complete';
  const failed = video.stream_status === 'error';

  return (
    <div className="film-admin-row">
      <button
        type="button"
        className="film-thumb-well"
        title="Set a custom thumbnail"
        onClick={() => fileRef.current?.click()}
      >
        {thumb ? <img src={thumb} alt="" /> : <span className="tiny">Thumb</span>}
        <span className="film-thumb-edit">{busy ? '…' : 'Edit'}</span>
      </button>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadThumb(e.target.files?.[0])} />

      <div className="grow">
        <input
          value={title}
          placeholder="Film title (e.g. Ceremony, Highlights)"
          onChange={(e) => setTitle(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
        <div className="tiny" style={{ marginTop: '0.3rem' }}>
          {saved ? 'Saved' : failed ? '⚠ Encoding failed' : ready ? '● Ready' : '◐ Processing…'} · {formatBytes(video.size_bytes)}
        </div>
      </div>

      <button className="sm danger" onClick={() => remove(video.id, onDeleted)}>
        Delete
      </button>
    </div>
  );
}

/**
 * The 8-character code the couple type into the TV or phone app. Not a secret
 * in the way the password is — it is stored as-is, so it is always shown here
 * and can be copied again any time. Regenerating locks out every TV and phone
 * already using the old code (their tokens are re-checked on each request).
 */
function AccessCodePanel({ gallery, patch, onChanged }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const enabled = gallery.access_code_enabled !== 0;
  const code = gallery.access_code || '';

  async function regenerate() {
    if (
      !window.confirm(
        'Make a new app code for this gallery?\n\n' +
          'The current code stops working immediately, including on any TV or phone ' +
          'that is already signed in. You will need to send the couple the new one.',
      )
    )
      return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/admin/galleries/${gallery.id}/access-code`);
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="field" style={{ marginBottom: 0 }}>
      <label>TV &amp; phone app code</label>
      <div className="row" style={{ alignItems: 'center' }}>
        <code className={`access-code ${enabled ? '' : 'is-off'}`}>{code ? `${code.slice(0, 4)} ${code.slice(4)}` : '—'}</code>
        <button
          type="button"
          className="sm"
          disabled={!code}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(code);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              /* clipboard blocked - the code is on screen to type */
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" className="sm danger" disabled={busy} onClick={regenerate}>
          {busy ? 'Working…' : 'Regenerate'}
        </button>
        <button type="button" className="sm ghost" onClick={() => patch({ access_code_enabled: !enabled })}>
          {enabled ? 'Disable' : 'Enable'}
        </button>
      </div>
      <div className="row" style={{ alignItems: 'center', marginTop: '0.5rem' }}>
        <label htmlFor="acx" className="tiny" style={{ margin: 0 }}>Code expires</label>
        <input
          id="acx"
          type="date"
          style={{ width: 'auto' }}
          value={gallery.access_code_expires_at || ''}
          onChange={(e) => patch({ access_code_expires_at: e.target.value })}
        />
        {gallery.access_code_expires_at && (
          <button type="button" className="sm ghost" onClick={() => patch({ access_code_expires_at: '' })}>
            Clear
          </button>
        )}
      </div>
      <p className="tiny" style={{ margin: '0.35rem 0 0' }}>
        {enabled
          ? 'The couple open the Android, Android TV or Samsung TV app and type this code — no password needed. The gallery expiry above still applies.'
          : 'Off — the app code is refused and any TV or phone using it is signed out. The web link and password are not affected.'}
      </p>
      {error && <div className="error" style={{ marginTop: '0.5rem' }}>{error}</div>}
    </div>
  );
}
